import Vue from 'vue';
import Router from 'vue-router';
import store from './stores/store';

Vue.use(Router);

function loadView(view: string) {
  return () => import(/* webpackChunkName: "view-[request]" */ `@/views/${view}.vue`);
}

enum EUserRoles {
  Unknown,
  Admin,
  Owner,
  Member,
}

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'Home',
      component: loadView('Shares'),
      meta: {
        permissions: [ EUserRoles.Admin ],
      }
    },
    {
      path: '/about',
      name: 'about',
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () =>
        import(/* webpackChunkName: "about" */ './views/About.vue')
    },
    {
      path: '/Login',
      name: 'Login',
      component: loadView('Login'),
    },
    {
      path: '/UserDetails/:Id',
      name: 'UserDetails',
      component: loadView('UserDetails'),
      props: true,
      meta: {
        permissions: [ EUserRoles.Admin, EUserRoles.Owner, EUserRoles.Member ],
      }
    },
    {
      path: '/Shares',
      name: 'Shares',
      component: loadView('Shares'),
      meta: {
        permissions: [ EUserRoles.Admin ],
      },
    },
    {
      path: '/Share/:Id',
      name: 'ShareHistory',
      component: loadView('ShareHistory'),
      props: true,
      meta: {
        permissions: [ EUserRoles.Admin ],
      },
    },
    {
      path: '/Members',
      name: 'Members',
      component: loadView('Members'),
      meta: {
        permissions: [ EUserRoles.Admin ],
      },
    },
    {
      path: '/Members/:Id',
      name: 'MemberDetails',
      component: loadView('MemberDetails'),
      meta: {
        permissions: [EUserRoles.Admin, EUserRoles.Owner, EUserRoles.Member ],
      },
    },

    {
      path: '/Shareholders',
      name: 'Shareholders',
      component: loadView('Shareholders'),
      meta: {
        permissions: [ EUserRoles.Admin ],
      },
    },
    {
      path: '/Shareholders/:Id',
      name: 'ShareholderDetails',
      component: loadView('ShareholderDetails'),
      meta: {
        permissions: [ EUserRoles.Admin, EUserRoles.Owner],
      }
    },
    {
      path: '/Cars',
      name: 'Cars',
      component: loadView('Cars'),
      meta: {
        permissions: [ EUserRoles.Admin ],
      },
    },
    {
      path: '/Cars/:Id',
      name: 'CarDetails',
      component: loadView('CarDetails'),
      meta: {
        permissions: [ EUserRoles.Admin ],
      },
    },
    {
      path: '/Contracts',
      name: 'Contracts',
      component: loadView('Contracts'),
      meta: {
        permissions: [ EUserRoles.Admin ],
      }
    },
    {
      path: '/Contracts/:CarNumber/:Id?',
      name: 'CarContractDetails',
      component: loadView('CarContractDetails'),
      meta: {
        permissions: [ EUserRoles.Admin ],
      }
    },
    {
      path: '/Profile',
      name: 'ProfileDetails',
      component: loadView('ProfileDetails'),
      meta: {
        permissions: [ EUserRoles.Admin, EUserRoles.Owner, EUserRoles.Member ],
      },
    },
    {
      path: '/Profiles',
      name: 'Profiles',
      component: loadView('Profiles'),
      meta: {
        permissions: [ EUserRoles.Admin, EUserRoles.Owner, EUserRoles.Member ],
      },
    },
    {
      path: '/News',
      name: 'News',
      component: loadView('News'),
      meta: {
        permissions: [ EUserRoles.Admin, EUserRoles.Owner, EUserRoles.Member ],
      },
    },
    {
      path: '/SharedDocuments',
      name: 'SharedDocuments',
      component: loadView('SharedDocuments'),
      meta: {
        permissions: [ EUserRoles.Admin, EUserRoles.Owner, EUserRoles.Member ],
      },
    },
    {
      path: '/ForgotPassword',
      name: 'ForgotPassword',
      component: loadView('ForgotPassword'),
    },
    {
      path: '/NewUser',
      name: 'NewUser',
      component: loadView('ForgotPassword'),
    },
    {
      path: '/ResetPassword',
      name: 'ResetPassword',
      component: loadView('ResetPassword'),
    },
  ]
});

router.beforeEach(async (to, from, next) => {
  // // If App is not initialized, initialize
  // // Used for refreshes
  if (from.name === null && !store.state.app.initialized) {
    await store.dispatch('app/initializeApp');
  }

  // Set correct locale if url contains lang=FI
  if (to.query.lang) {
    store.commit('app/changeLocale', to.query.lang);
  }
  // Redirect to login is user is not logged in
  if (to.name !== 'Login' && !store.getters['auth/isAuthenticated']) {
    if (to.name === 'ForgotPassword' || to.name === 'ResetPassword' || to.name === 'NewUser') {
      next();
    } else {
      next({
        name: 'Login',
        query: { redirect: to.fullPath },
      });
    }
  }

  if (to.path === '/Logout') {
    await store.dispatch('auth/logout');
    next({ name: 'Login' });
  }

  // TODO: Remove hack, make smarter.
  if (to.meta.permissions) {
    const user = store.state.auth.profile;
    const hasPermissions = (userProfile: IUserAuthProfile) => {
      if (userProfile.isAdmin && to.meta.permissions.indexOf(EUserRoles.Admin) >= 0) {
        return true;
      }
      if (userProfile.isOwner && to.meta.permissions.indexOf(EUserRoles.Owner) >= 0) {
        return true;
      }
      if (userProfile.isMember && to.meta.permissions.indexOf(EUserRoles.Member) >= 0) {
        return true;
      }
    };
    if (user && !hasPermissions(user)) {
      next({ name: 'News' });
    }
  }

  next();
});

export default router;
